import { useCallback, useMemo, useState } from 'react';
import { useSuspenseQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { availabilityWindows, mediaUnits, reservationQueue } from '@ooh/contracts';
import { apiQueryOptions } from '../data/use-api-suspense-query';
import { QueryBoundary } from '../layouts/query-boundary';
import { TableSkeleton } from '../layouts/skeletons';
import { useApiClient } from '../plugs/api-client';
import { useCurrentUser } from '../plugs/current-user';
import {
  queueByUnitRequest,
  unitAuditEventsRequest,
  unitAvailabilityWindowsRequest,
  unitReservationsRequest,
} from './api/queries';
import { useCreateAvailabilityWindow } from './api/mutations';
import { AvailabilityWindowList } from './components/availability-window-list';
import type { AvailabilityBlockDialogValues } from './components/maintenance-block-dialog';
import type { ActiveDialog } from './components/reservation-action-dialogs';
import type { QueueAction, QueueEntryView } from './components/reservation-queue-list';
import { UnitReservationsTab } from './components/unit-reservations-tab';
import { useReservationActions } from './hooks/use-reservation-actions';
import { computeForceFlag, useReservationPermissions } from './hooks/use-reservation-permissions';
import { useQueueActions } from './hooks/use-reservation-queue-panel';
import { parseReservationFocusParam } from './lib/formatters';
import { mapQueueEntriesToViews } from './lib/queue-entry-view';
import {
  buildAvailabilityWindowListLabels,
  buildUnitQueueLabels,
  buildUnitReservationsTabLabels,
} from './lib/reservation-labels';
import type { TranslateWithDefault } from './lib/reservation-labels';
import {
  buildOpenFullQueueSearch,
  makeAvailabilityWindowMapper,
  makeUnitRowMapper,
  mapAuditEvent,
} from './lib/unit-panel-mappers';
import type { OpenFullQueueSearch } from './lib/unit-panel-mappers';
import { resolvePromoteTarget } from './lib/resolve-promote-target';
import { ReservationDialogsHost } from './reservation-dialogs-host';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

export interface UnitReservationsPanelProps {
  unitId: number;
  /** Shown in the action dialogs (the unit's GRW id). */
  mediaUnitName: string;
  /** Raw `?focus=` value, e.g. `reservation:1009`; that row is scrolled to and highlighted. */
  focus: string | null;
  onOpenFullQueue: (search: OpenFullQueueSearch) => void;
}

function parseFocusId(focus: string | null): number | null {
  const parsed = focus ? parseReservationFocusParam(focus) : null;
  return parsed !== null ? parseInt(parsed, 10) : null;
}

function UnitReservationsPanelContent({
  unitId,
  mediaUnitName,
  focus,
  onOpenFullQueue,
}: UnitReservationsPanelProps) {
  const { t } = useTranslation('reservations');
  const translate = t as Translate;
  const api = useApiClient();
  const { user } = useCurrentUser();
  const permissions = useReservationPermissions();

  const [reservationsQuery, auditQuery, queueQuery, windowsQuery] = useSuspenseQueries({
    queries: [
      apiQueryOptions(api, mediaUnits.listReservations, unitReservationsRequest(unitId)),
      apiQueryOptions(api, mediaUnits.listAuditEvents, unitAuditEventsRequest(unitId)),
      apiQueryOptions(api, reservationQueue.listPosition, queueByUnitRequest(unitId)),
      apiQueryOptions(api, availabilityWindows.list, unitAvailabilityWindowsRequest(unitId)),
    ],
  });

  const unitRows = useMemo(
    () => reservationsQuery.data.rows.map(makeUnitRowMapper(translate)),
    [reservationsQuery.data, translate],
  );
  const auditEntries = useMemo(
    () => auditQuery.data.events.map((event) => mapAuditEvent(event, translate)),
    [auditQuery.data, translate],
  );
  const windowRows = useMemo(
    () => windowsQuery.data.rows.map(makeAvailabilityWindowMapper(t as (key: string) => string)),
    [windowsQuery.data, t],
  );
  // A queue entry belongs to a reservation when their windows overlap, so one entry may sit under several.
  const queueByReservationId = useMemo<Record<number, QueueEntryView[]>>(() => {
    const entries = queueQuery.data.items;
    if (entries.length === 0) return {};
    return Object.fromEntries(
      unitRows.map((res) => [res.id, mapQueueEntriesToViews(entries, res, unitRows)]),
    );
  }, [queueQuery.data, unitRows]);

  const mediaUnitIdByReservationId = useMemo(
    () => new Map(unitRows.map((row) => [row.id, unitId])),
    [unitRows, unitId],
  );
  const queue = useQueueActions(mediaUnitIdByReservationId);

  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
  const [activeReservationId, setActiveReservationId] = useState<number | null>(null);
  const activeReservation = unitRows.find((r) => r.id === activeReservationId) ?? null;
  const { dispatch, isPending: isActionPending } = useReservationActions();

  const [isAvailabilityDialogOpen, setIsAvailabilityDialogOpen] = useState(false);
  const createAvailabilityWindow = useCreateAvailabilityWindow();

  const handleAction = useCallback((action: string, reservationId: number) => {
    setActiveReservationId(reservationId);
    setActiveDialog(action as ActiveDialog);
  }, []);

  const handleConfirmAction = useCallback(
    (action: string, dialogData?: Record<string, unknown>) => {
      if (activeReservationId === null || !activeReservation) return;
      // force=true only for other users' rows, and only for a force_release holder (CU-86can6mmu).
      const force = computeForceFlag(activeReservation, user?.id ?? 0, permissions.canForceRelease);
      dispatch(
        action,
        { id: activeReservationId, status: activeReservation.status, unitId, force },
        dialogData,
        { onSuccess: () => setActiveDialog(null) },
      );
    },
    [
      activeReservationId,
      activeReservation,
      unitId,
      dispatch,
      user?.id,
      permissions.canForceRelease,
    ],
  );

  const handleCreateAvailabilityWindow = useCallback(
    (values: AvailabilityBlockDialogValues) => {
      createAvailabilityWindow.mutate(
        { unitId, ...values },
        { onSuccess: () => setIsAvailabilityDialogOpen(false) },
      );
    },
    [createAvailabilityWindow, unitId],
  );

  const handleOpenFullQueue = useCallback(
    (reservationId: number) => {
      const search = buildOpenFullQueueSearch(unitId, unitRows, reservationId);
      if (search) onOpenFullQueue(search);
    },
    [onOpenFullQueue, unitRows, unitId],
  );

  const promoteTarget = resolvePromoteTarget(
    queue.promoteTarget,
    unitRows,
    queueByReservationId,
    mediaUnitName,
  );

  return (
    <>
      <UnitReservationsTab
        reservations={unitRows}
        auditEntries={auditEntries}
        focusReservationId={parseFocusId(focus)}
        permissions={permissions}
        labels={buildUnitReservationsTabLabels(translate)}
        currentUserId={user?.id ?? 0}
        onAction={handleAction}
        onBlockForMaintenance={() => setIsAvailabilityDialogOpen(true)}
        canBlockForMaintenance={permissions.canBlockAvailability}
        queueByReservationId={queueByReservationId}
        queueLabels={buildUnitQueueLabels(t as TranslateWithDefault)}
        onQueueAction={(action: QueueAction, entryId: number, reservationId: number) =>
          queue.handleQueueAction(action, entryId, reservationId)
        }
        onOpenFullQueue={handleOpenFullQueue}
        openFullQueueLabel={t('queueOverview.openFullQueueLabel')}
      />
      <AvailabilityWindowList
        windows={windowRows}
        total={windowsQuery.data.total}
        labels={buildAvailabilityWindowListLabels(translate)}
      />
      <ReservationDialogsHost
        action={{
          dialog: activeDialog,
          target: activeReservation && {
            id: activeReservation.id,
            mediaUnitId: unitId,
            mediaUnitName,
            dateFrom: activeReservation.dateFrom,
            dateTo: activeReservation.dateTo,
            effectiveExpiresAt: activeReservation.effectiveExpiresAt,
          },
          isAdmin: permissions.isAdmin,
          isPending: isActionPending,
          onClose: () => setActiveDialog(null),
          onConfirm: handleConfirmAction,
        }}
        availability={{
          isOpen: isAvailabilityDialogOpen,
          isPending: createAvailabilityWindow.isPending,
          onClose: () => setIsAvailabilityDialogOpen(false),
          onSave: handleCreateAvailabilityWindow,
        }}
        promote={{
          target: promoteTarget,
          isPending: queue.isPromotePending,
          onClose: () => queue.setPromoteTarget(null),
          onConfirm: queue.handlePromoteConfirm,
        }}
      />
    </>
  );
}

export function UnitReservationsPanel(props: UnitReservationsPanelProps) {
  return (
    <QueryBoundary fallback={<TableSkeleton />}>
      <UnitReservationsPanelContent {...props} />
    </QueryBoundary>
  );
}
